import moment from "moment-timezone";
import { pick } from "lodash";
import { getOrderedWeekdays } from "../getOrderedWeekdays";
import { calculateSectionsWithAutomaticBreaks, getAutomaticBreaks, getSectionsBounds, recalculateSections } from "./sections";
import { combineDateAndTime, renderDurationAsFormat } from "../../../../../utils/helpers/date";
import { weekdays } from "../../../../../components/Inputs/WeekdaySelect";
import { getTargetDates } from "../data/formatTemplateEvents";
import { getResourceUserId } from "../data/groupedResourceId";

export const calculateFormValues = (values: any, selectedCellOrEvent: any, defaultSection: any) => {
    const orderedWeekdays = getOrderedWeekdays();
    const isStrictDate = moment(values?.date, "YYYY-MM-DD", true).isValid();

    const date = selectedCellOrEvent?.isTemplate
        ? (isStrictDate ? orderedWeekdays[moment(values.date).weekday()] : values.date)
        : moment(values.date).format("YYYY-MM-DD");

    const _date = isStrictDate
        ? moment(values.date).format("YYYY-MM-DD")
        : moment().weekday(orderedWeekdays.indexOf(values.date)).format("YYYY-MM-DD");

    // Fall back to the widest span the sections cover, not the first/last row - a section
    // ending after the rows below it would otherwise be cut off at save time.
    const sectionsBounds = getSectionsBounds(values?.sections);
    let startDate = combineDateAndTime(moment(_date), moment(values?.startDate || sectionsBounds.start));
    let endDate = combineDateAndTime(moment(_date), moment(values?.endDate || sectionsBounds.end));
    if (/**values.start && values.end && */ moment(endDate).isBefore(moment(startDate), "seconds")) {
        endDate = endDate.add(1, "day");
    }
    const isMandatoryBreak = selectedCellOrEvent?.user?.calculationGroup?.breaks?.manual?.mandatory;
    const breakAmount = isMandatoryBreak
        ? Number(selectedCellOrEvent?.user?.calculationGroup?.breaks?.manual?.duration || 0)
        : 0;
    let _sections: any = values?.sections || selectedCellOrEvent?.sections || [];

    _sections = _sections.map((section: any) => {
        const start = typeof section.start === "number"
            ? moment(startDate).add(section.start, "seconds")
            : combineDateAndTime(moment(_date), moment(section.start));
        let end = typeof section.end === "number"
            ? moment(startDate).add(section.end, "seconds")
            : combineDateAndTime(moment(_date), moment(section.end));
        if (moment(end).isBefore(moment(start), "seconds")) {
            end = moment(end).add(1, "day");
        }
        return {
            ...section,
            start,
            end
        }
    });

    if (values.isSimple && (selectedCellOrEvent?.isCreating || selectedCellOrEvent?.addAnotherEvent || values.breaks?.length > 0)) {
        const commonProps = {
            job: values.job,
            location: values.location,
            skills: values.skills,
            licenses: values.licenses,
            certifications: values.certifications,
            ...pick(values, ["level1", "level2", "level3", "level4"]),
            canCalculateAutomaticBreaks: selectedCellOrEvent?.isCreating || selectedCellOrEvent?.addAnotherEvent
        };

        // Helper to generate a standardized section object
        const createSection = (type: any, start: any, end: any, payCode: any) => ({
            type,
            start,
            end,
            payCode,
            duration: renderDurationAsFormat(moment(end).diff(moment(start), "seconds"), "HH:mm"),
            ...commonProps
        });

        // 1. Normalize and Sort Breaks
        const breaks = (values.breaks || [])
            .map((item: any) => {
                const bStart = typeof item.start === "number"
                    ? moment(startDate).add(item.start, "seconds")
                    : combineDateAndTime(moment(_date), moment(item.start));
                let bEnd = typeof item.end === "number"
                    ? moment(startDate).add(item.end, "seconds")
                    : combineDateAndTime(moment(_date), moment(item.end));

                if (moment(bEnd).isBefore(bStart)) {
                    bEnd = moment(bEnd).add(1, "day");
                }

                // Skip breaks that start at or after shift end, or end at or before shift start
                if (!moment(bStart).isBefore(endDate) || !moment(bEnd).isAfter(startDate)) return null;

                return createSection("break", bStart, bEnd, item.payCode ?? defaultSection.payCode);
            })
            .filter(Boolean)
            .sort((a: any, b: any) => moment(a.start).diff(moment(b.start)));

        // 2. Build Timeline (Interleave Regular work with Breaks)
        const sections = [];
        let cursor = startDate;

        breaks.forEach((br: any) => {
            // Add Regular work before the break if time exists
            if (moment(br.start).isAfter(cursor)) {
                sections.push(createSection("regular", cursor, br.start, values.payCode));
            }

            // Add the Break
            sections.push(br);
            cursor = br.end;
        });

        // 3. Add final Regular work segment if time remains
        if (moment(endDate).isAfter(cursor)) {
            sections.push(createSection("regular", cursor, endDate, values.payCode));
        }

        _sections = sections;
    }

    if (values?.isSimple && !selectedCellOrEvent?.isCreating && !selectedCellOrEvent?.addAnotherEvent) {
        _sections = _sections.map((section: any, index: number) => ({
            ...section,
            start: index === 0 ? moment(startDate) : section.start,
            end: index === _sections.length - 1 ? moment(endDate) : section.end,
            // Break sections keep their own pay code; the work sections take the one picked in the form
            payCode: section?.type === "break" ? section.payCode : (values.payCode ?? section.payCode),
            job: values.job,
            location: values.location,
            skills: values.skills,
            licenses: values.licenses,
            certifications: values.certifications,
            ...pick(values, ["level1", "level2", "level3", "level4"]),
        }));
    }
    let sections: any = recalculateSections(
        _sections,
        defaultSection,
        breakAmount * 60,
        selectedCellOrEvent?.user?.calculationGroup?.breaks,
        startDate
    );
    let breaks = getAutomaticBreaks(
        selectedCellOrEvent?.user?.calculationGroup?.breaks,
        sections,
        defaultSection
    );
    const sectionBreaks = sections.filter((section: any) => section?.type === "break");
    breaks = (breaks || []).slice(sectionBreaks.length);

    let calculatedSections = sections;
    if (selectedCellOrEvent?.isCreating || selectedCellOrEvent?.addAnotherEvent) {
        let breaks = getAutomaticBreaks(
            selectedCellOrEvent?.user?.calculationGroup?.breaks,
            sections,
            defaultSection
        );
        const sectionBreaks = sections.filter((section: any) => section?.type === "break");
        breaks = (breaks || []).slice(sectionBreaks.length);

        if (breaks.length > 0 && selectedCellOrEvent?.user?.calculationGroup?.breaks?.automatic?.status) {
            const lastBreakIndex = sections.findLastIndex((shift: any) => shift.type === "break");
            calculatedSections = [
                ...sections.slice(0, lastBreakIndex + 1),
                ...calculateSectionsWithAutomaticBreaks(sections.slice(lastBreakIndex + 1), breaks)
            ];
        }
    } else {
        // On EDIT/RESIZE, just ensure sections stay within the new total shift duration
        calculatedSections = sections.map((section: any) => {
            if (weekdays.includes(values.date)) {
                const mappedDates = getTargetDates("week");
                values.date = mappedDates[values.date];
            }
            const sStart = combineDateAndTime(moment(values.date), moment(section.start));
            let sEnd = combineDateAndTime(moment(values.date), moment(section.end));
            if (moment(sEnd).isBefore(moment(sStart), "seconds")) {
                sEnd = moment(sEnd).add(1, "day");
            }
            // Clamp to new total start/end
            const finalStart = moment.max(sStart, startDate);
            const finalEnd = moment.min(sEnd, endDate);

            return {
                ...section,
                start: finalStart,
                end: finalEnd,
                duration: renderDurationAsFormat(moment(finalEnd).diff(finalStart, 'seconds'), "HH:mm")
            };
        }).filter((s: any) => moment(s.end).isAfter(s.start)); // Remove sections crushed to 0 duration
    }

    const levelKeys = ["level1", "level2", "level3", "level4"];
    const user = selectedCellOrEvent?.user;
    const userLevels = levelKeys.reduce((total: Record<string, any>, level) => {
        if (user?.[level]) total[level] = user[level];
        return total;
    }, {});

    calculatedSections = calculatedSections.map((section: any) => {
        const formLevels = levelKeys.reduce((total: Record<string, any>, level) => {
            if (section?.[level]) total[level] = section[level];
            return total;
        }, {});

        return {
            ...section,
            ...userLevels,
            ...formLevels,
        };
    });

    const formattedValues = {
        date,
        startDate,
        endDate,
        isSimple: values.isSimple,
        recurrence: values?.recurrence || selectedCellOrEvent?.recurrence || null,
        sections: calculatedSections,
        user: getResourceUserId(selectedCellOrEvent?.user?.id || selectedCellOrEvent?.resourceId)
    };

    return formattedValues;
}
